const MONEY_FORMATS = {
  '{{amount}}': (c) => (c / 100).toFixed(2),
  '{{amount_no_decimals}}': (c) => Math.round(c / 100).toString(),
  '{{amount_with_comma_separator}}': (c) => (c / 100).toFixed(2).replace('.', ','),
  '{{amount_no_decimals_with_comma_separator}}': (c) => Math.round(c / 100).toString(),
  '{{amount_with_space_separator}}': (c) => (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
  '{{amount_no_decimals_with_space_separator}}': (c) => Math.round(c / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
  '{{amount_with_apostrophe_separator}}': (c) => (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'"),
};

function formatMoney(cents, format) {
  const template = format || (window.Shopify && window.Shopify.moneyFormat) || '${{amount}}';
  for (const [placeholder, fn] of Object.entries(MONEY_FORMATS)) {
    if (template.includes(placeholder)) {
      let value = fn(cents);
      if (placeholder === '{{amount}}' || placeholder === '{{amount_with_comma_separator}}') {
        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, placeholder === '{{amount_with_comma_separator}}' ? '.' : ',');
      }
      return template.replace(placeholder, value);
    }
  }
  return template.replace('{{amount}}', (cents / 100).toFixed(2));
}

class ProductVariants extends HTMLElement {
  connectedCallback() {
    const dataScript = this.querySelector('[data-variants-data]');
    try {
      this.variants = JSON.parse(dataScript?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    const messagesScript = this.querySelector('[data-variant-messages]');
    try {
      this.variantMessages = JSON.parse(messagesScript?.textContent || '{}');
    } catch {
      this.variantMessages = {};
    }

    this.optionGroups = this.querySelectorAll('[data-option-group]');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.installmentsEl = this.querySelector('[data-installment]');
    this.installmentAmountEl = this.querySelector('[data-installment-amount]');
    this.inventoryEl = this.querySelector('[data-inventory]');
    this.atcButton = this.querySelector('[data-atc]');
    this.dynamicCheckoutEl = this.querySelector('[data-dynamic-checkout]');

    this.optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });
    });
  }

  getGroupValue(group) {
    const select = group.querySelector('select[data-option-input]');
    if (select) return select.value;
    const checked = group.querySelector('input[type="radio"][data-option-input]:checked');
    return checked ? checked.value : null;
  }

  onOptionChange() {
    const selectedValues = Array.from(this.optionGroups).map((g) => this.getGroupValue(g));
    const variant = this.variants.find((v) =>
      v.options.every((opt, i) => opt === selectedValues[i])
    );
    this.optionGroups.forEach((group, i) => {
      const label = group.querySelector('[data-option-selected-value]');
      if (label) label.textContent = selectedValues[i] || '';
    });
    if (!variant) return;
    this.updateUI(variant);
    this.updateUrl(variant);
  }

  updateUI(variant) {
    if (this.variantIdInput) this.variantIdInput.value = variant.id;

    if (this.priceEl) {
      this.priceEl.textContent = formatMoney(variant.price, window.Shopify?.moneyWithCurrencyFormat);
    }

    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = formatMoney(variant.compare_at_price);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }

    if (this.installmentsEl) {
      if (variant.price >= 5000) {
        if (this.installmentAmountEl) {
          this.installmentAmountEl.textContent = formatMoney(Math.floor(variant.price / 4));
        }
        this.installmentsEl.hidden = false;
      } else {
        this.installmentsEl.hidden = true;
      }
    }

    if (this.inventoryEl) {
      const threshold = parseInt(this.inventoryEl.dataset.threshold, 10) || 0;
      const tracked =
        variant.inventory_management && variant.inventory_policy !== 'continue';
      let state = 'in_stock';
      if (!variant.available) {
        state = 'out_of_stock';
      } else if (
        tracked &&
        variant.inventory_quantity > 0 &&
        variant.inventory_quantity <= threshold
      ) {
        state = 'low_stock';
      }

      const entry = this.variantMessages?.[variant.id] || {};
      const customMessage = entry.text || '';
      const customStyle = (entry.style || '').toLowerCase();
      const STYLE_STATES = { available: 'in_stock', limited: 'low_stock', unavailable: 'out_of_stock' };

      let displayState = state;
      let displayText = '';
      if (customMessage) {
        if (STYLE_STATES[customStyle]) {
          displayState = STYLE_STATES[customStyle];
          displayText = customMessage;
        } else if (state !== 'in_stock') {
          displayText = customMessage;
        }
      }

      const activeEl = this.inventoryEl.querySelector(
        `[data-state-key="${displayState}"]`
      );
      const textEl = activeEl?.querySelector('[data-text]');
      if (textEl) {
        if (displayText) {
          textEl.textContent = displayText;
        } else if (displayState === 'low_stock') {
          textEl.innerHTML = `Only <span data-quantity>${variant.inventory_quantity}</span> left in stock`;
        } else if (displayState === 'in_stock') {
          textEl.textContent = this.inventoryEl.dataset.inStockMessage || '';
        } else {
          textEl.textContent = 'Out of stock';
        }
      }

      this.inventoryEl
        .querySelectorAll('[data-state-key]')
        .forEach((el) => {
          el.hidden = el.dataset.stateKey !== displayState;
        });
    }

    if (this.atcButton) {
      this.atcButton.disabled = !variant.available;
      const label = variant.available
        ? this.atcButton.dataset.labelAvailable
        : this.atcButton.dataset.labelUnavailable;
      if (label) this.atcButton.textContent = label;
    }

    if (this.dynamicCheckoutEl) this.dynamicCheckoutEl.hidden = !variant.available;

    this.dispatchEvent(new CustomEvent('variant:change', { detail: { variant }, bubbles: true }));
  }

  updateUrl(variant) {
    const base = this.dataset.url;
    if (!base || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }
}

customElements.define('product-variants', ProductVariants);
